import { PermissionEnum } from '@Application/Security/Roles';
import { openStaffMemberSidebar } from '@UI/Pages/Agencies/StaffMembers/Signals/staffMemberSidebarSignal';
import { setStaffMemberToDelete } from '@UI/Pages/Agencies/StaffMembers/Signals/staffMemberToDeleteSignal';
import type StaffMemberView from '@UI/Store/StaffMembers/Views/StaffMemberView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-top: auto;
`;

type Props = {
	readonly staffMember: StaffMemberView;
};

/**
 * Leaf action component: it opens the edit sidebar and requests the delete confirmation purely
 * through their signals' setters — the page owns both overlays and reacts to the same signals.
 * "Modifier" and "Supprimer" are gated INDEPENDENTLY (`STAFF_WRITE` vs `STAFF_DELETE`): the two
 * permissions sit at different tiers, so a caller can hold one without the other.
 */
const StaffMemberCardActions: React.FC<Props> = ({ staffMember }) => {
	const handleEditClick = React.useCallback(() => {
		openStaffMemberSidebar(staffMember.id);
	}, [staffMember.id]);

	const handleDeleteClick = React.useCallback(() => {
		setStaffMemberToDelete(staffMember.id);
	}, [staffMember.id]);

	return (
		<div css={actionsRowStyle}>
			<WithPermission permission={PermissionEnum.STAFF_WRITE}>
				<Button
					type="button"
					size="sm"
					aria-label={`Modifier ${staffMember.fullName}`}
					onClick={handleEditClick}
					severity={'secondary'}
				>
					Modifier
				</Button>
			</WithPermission>
			<WithPermission permission={PermissionEnum.STAFF_DELETE}>
				<Button
					type="button"
					size="sm"
					severity={'danger'}
					outlined
					aria-label={`Supprimer ${staffMember.fullName}`}
					onClick={handleDeleteClick}
				>
					Supprimer
				</Button>
			</WithPermission>
		</div>
	);
};

export default StaffMemberCardActions;
